import { useAtomValue } from 'jotai';
import { createContext, useMemo, useState } from 'react';

import { useTablePagination } from '@/lib/components/Table/useTablePagination';

import { atomTotalsProcess } from '@/src/modules/CampaignsModule/atoms/campaignAtom';
import {
  IChildProps,
  IFilterByTable,
  IMutationContext,
  IStateContext,
} from '@/src/modules/CampaignsModule/interfaces/CampaignActions';
import { IOrderByTable } from '@/src/modules/CampaignsModule/interfaces/Campaigns';
import { useGetContext } from '@/src/utils/ContextUtils';

const CampaignActionsStateContext = createContext<IStateContext | undefined>(undefined);
const CampaignActionsMutationContext = createContext<IMutationContext | undefined>(undefined);

const CampaignActionsContext = ({ children }: IChildProps) => {
  const totalsCampaignList = useAtomValue(atomTotalsProcess);
  const [orderBy, setOrderBy] = useState<IOrderByTable>({
    sortField: 'creationDate',
    sortOrder: 'desc',
  });
  const [clearFilterBy, setClearFilterBy] = useState<boolean>(false);
  const [filterBy, setFilterBy] = useState<IFilterByTable>({});

  const { pageSize, pageIndex, totalPages, gotoPageIndex, changeTablePage, resetPagination } =
    useTablePagination(totalsCampaignList.partial);

  const memoizedMutations: IMutationContext = useMemo<IMutationContext>(
    () => ({
      changeTablePage,
      resetPagination,
      setClearFilterBy,
      setFilterBy,
      setOrderBy,
    }),
    [changeTablePage, resetPagination, setClearFilterBy, setFilterBy, setOrderBy]
  );

  const state: IStateContext = useMemo<IStateContext>(
    () => ({
      pageSize,
      pageIndex,
      totalPages,
      gotoPageIndex,
      clearFilterBy,
      filterBy,
      orderBy,
    }),
    [pageSize, pageIndex, totalPages, gotoPageIndex, clearFilterBy, filterBy, orderBy]
  );

  return (
    <CampaignActionsMutationContext.Provider value={memoizedMutations}>
      <CampaignActionsStateContext.Provider value={state}>
        {children}
      </CampaignActionsStateContext.Provider>
    </CampaignActionsMutationContext.Provider>
  );
};

export const useCampaignActionsMutationContext = (): IMutationContext =>
  useGetContext<IMutationContext>(CampaignActionsMutationContext, 'CampaignActionsMutationContext');

export const useCampaignActionsStateContext = (): IStateContext =>
  useGetContext<IStateContext>(CampaignActionsStateContext, 'CampaignActionsStateContext');

export default CampaignActionsContext;

//Campaigns
